import React, { createContext, useContext, useState } from "react";
import { INTRO_MESSAGE, ArtifactVizType } from "./utils";
import type { Message, ConversationSummary, Citation, StepRecord, StepFrame } from "./types";
import { applyStepFrame } from "./utils/stepUtils";
import type { DrawerMode } from "./utils/debugPanelUtils";

export type { Message, ConversationSummary };

interface Conversation {
    id: number;
    messages: Message[];
    stepsByTurn: Record<string, StepRecord[]>;
    openQuestionIds: string[];
}

interface ChatContextValue {
    messages: Message[];
    // The question ids the coach can answer now: turn_done.open, or trace.open on a reload.
    // A control is live only for an open id, on the latest card or question row of that id.
    openQuestionIds: string[];
    setOpenQuestionIds: React.Dispatch<React.SetStateAction<string[]>>;
    appendNewMessage: (message: Message) => void;
    conversationId: number | null;
    setConversationId: (id: number) => void;
    setConversation: (conversation: Conversation) => void;
    resetConversation: () => void;
    // Bumped only by setConversation/resetConversation (an actual switch), never by the plain
    // conversationId fill-in that happens mid-turn once the backend assigns a brand-new
    // conversation's id. ChatInput resets its staged text/images on this, not on conversationId,
    // so prepping the next message while the first turn is still streaming doesn't get wiped.
    conversationEpoch: number;
    conversations: ConversationSummary[];
    setConversations: React.Dispatch<React.SetStateAction<ConversationSummary[]>>;
    // Strip the fresh flags so reveal animations don't replay when a consumer (the
    // floating panel) unmounts and remounts MessageList over the same messages.
    markMessagesSeen: () => void;
    isTyping: boolean;
    setIsTyping: React.Dispatch<React.SetStateAction<boolean>>;
    // Step records per turn, keyed by the backend message_id. Live frames and the reload both
    // write here, so one accordion component renders either.
    stepsByTurn: Record<string, StepRecord[]>;
    startTurn: (turnId: string) => void;
    applyStep: (frame: StepFrame) => void;
    activeTurnId: string | null;
    clearActiveTurn: () => void;
    streamingCaption: string;
    appendCaptionDelta: (delta: string) => void;
    clearStreamingCaption: () => void;
    pendingText: string;
    appendPendingText: (text: string) => void;
    clearPendingText: () => void;
    attachCitationsToLastAnswer: (citations: Citation[], since: number) => void;
    pendingVizType: ArtifactVizType | null;
    setPendingVizType: React.Dispatch<React.SetStateAction<ArtifactVizType | null>>;
    agentModel: string | null;
    setAgentModel: React.Dispatch<React.SetStateAction<string | null>>;
    selectedMessage: Message | null;
    setSelectedMessage: React.Dispatch<React.SetStateAction<Message | null>>;
    drawerMode: DrawerMode;
    setDrawerMode: React.Dispatch<React.SetStateAction<DrawerMode>>;
}

const ChatContext = createContext<ChatContextValue>({} as ChatContextValue);

export const useChatContext = () => useContext(ChatContext);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [messages, setMessages] = useState<Message[]>([INTRO_MESSAGE]);
    const [openQuestionIds, setOpenQuestionIds] = useState<string[]>([]);
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [stepsByTurn, setStepsByTurn] = useState<Record<string, StepRecord[]>>({});
    const [activeTurnId, setActiveTurnId] = useState<string | null>(null);
    const [streamingCaption, setStreamingCaption] = useState("");
    const [pendingText, setPendingText] = useState("");
    const [pendingVizType, setPendingVizType] = useState<ArtifactVizType | null>(null);
    const [agentModel, setAgentModel] = useState<string | null>(null);
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
    const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
    const [conversationEpoch, setConversationEpoch] = useState(0);

    // Resets the key so a repeated message_id cannot merge two turns. The backend reuses
    // "unsaved-turn0-assistant" on every send while storage is down, and without the reset the
    // second turn's closes would pair against the first turn's records.
    const startTurn = (turnId: string) => {
        setStepsByTurn(prev => ({ ...prev, [turnId]: [] }));
        setActiveTurnId(turnId);
        // Unique per send, not per turn: while storage is down two anchor rows would otherwise
        // share a React key. Nothing reads this id; the accordion reads metadata.turnId.
        appendNewMessage({
            id: `${turnId}-steps-${Date.now()}`, role: "assistant", type: "steps", content: "",
            created: Date.now(), metadata: { turnId },
        });
    };

    const applyStep = (frame: StepFrame) =>
        setStepsByTurn(prev => ({ ...prev, [frame.message_id]: applyStepFrame(prev[frame.message_id] ?? [], frame) }));

    const clearActiveTurn = () => setActiveTurnId(null);

    const appendCaptionDelta = (delta: string) => setStreamingCaption(prev => prev + delta);
    const clearStreamingCaption = () => setStreamingCaption("");
    const appendPendingText = (text: string) => setPendingText(prev => prev + text);
    const clearPendingText = () => setPendingText("");

    // Patches citations onto an already-committed answer (render/summary paths, where the
    // citations frame arrives after the message is flushed). Prefers the turn's caption/summary
    // message; falls back to the turn's last assistant message (e.g. a chart with no caption)
    // so citations aren't silently dropped when a render-path turn never streamed any text.
    // No-op if no assistant message exists this turn at all.
    //
    // Citations are a supporting feature, not the answer itself — this runs inside a setMessages
    // updater (not React render), so a React error boundary can't catch a throw here. Left
    // uncaught, it would propagate up through the SSE read loop in useChatTurn's sendMessage and
    // fail the *whole turn*, even though the actual answer already streamed fine. The try/catch
    // keeps a citations bug scoped to "this turn has no citations," never to "the turn failed."
    const attachCitationsToLastAnswer = (citations: Citation[], since: number) => {
        setMessages(prev => {
            try {
                const inTurn = (m: Message) => m.created >= since && m.role === "assistant";
                const captionIdx = prev.findLastIndex(m => inTurn(m) && (m.type === "text" || m.type === "summary"));
                const idx = captionIdx !== -1 ? captionIdx : prev.findLastIndex(inTurn);
                if (idx === -1) return prev;
                const next = [...prev];
                next[idx] = { ...prev[idx], metadata: { ...prev[idx].metadata, citations } };
                return next;
            } catch (e) {
                console.error("[Chatbot] Failed to attach citations:", e);
                return prev;
            }
        });
    };

    // Returns the same array when nothing is fresh, so effects depending on this
    // running while the panel is closed don't trigger render loops.
    const markMessagesSeen = () =>
        setMessages(prev =>
            prev.some(m => m.fresh) ? prev.map(m => (m.fresh ? { ...m, fresh: false } : m)) : prev,
        );

    const appendNewMessage = (newMessage: Message) => {
        // Tag live-streamed messages so MessageList animates them once on arrival. History loaded via
        // setConversation goes straight through setMessages and stays unflagged (no animation on load).
        setMessages(prev => [...prev, { ...newMessage, fresh: true }]);
    };

    const setConversation = (conversation: Conversation) => {
        setConversationId(conversation.id);
        setMessages(conversation.messages);
        setOpenQuestionIds(conversation.openQuestionIds);
        setStepsByTurn(conversation.stepsByTurn);
        setActiveTurnId(null);
        // Load-bearing, not tidying. A switch mid-turn makes that turn stale, and useChatTurn's
        // guard then stops it running its own setIsTyping(false), so the spinner would hang.
        setIsTyping(false);
        setStreamingCaption("");
        setPendingText("");
        setPendingVizType(null);
        setSelectedMessage(null);
        setDrawerMode(null);
        setConversationEpoch(prev => prev + 1);
    };

    const resetConversation = () => {
        setConversationId(null);
        setMessages([INTRO_MESSAGE]);
        setOpenQuestionIds([]);
        setStepsByTurn({});
        setActiveTurnId(null);
        setIsTyping(false);
        setStreamingCaption("");
        setPendingText("");
        setPendingVizType(null);
        setSelectedMessage(null);
        setDrawerMode(null);
        setConversationEpoch(prev => prev + 1);
    };

    return (
        <ChatContext.Provider value={{
            messages,
            openQuestionIds,
            setOpenQuestionIds,
            appendNewMessage,
            conversationId,
            setConversationId,
            setConversation,
            resetConversation,
            conversations,
            setConversations,
            markMessagesSeen,
            isTyping,
            setIsTyping,
            stepsByTurn,
            startTurn,
            applyStep,
            activeTurnId,
            clearActiveTurn,
            streamingCaption,
            appendCaptionDelta,
            clearStreamingCaption,
            pendingText,
            appendPendingText,
            clearPendingText,
            attachCitationsToLastAnswer,
            pendingVizType,
            setPendingVizType,
            agentModel,
            setAgentModel,
            selectedMessage,
            setSelectedMessage,
            drawerMode,
            setDrawerMode,
            conversationEpoch,
        }}>
            {children}
        </ChatContext.Provider>
    );
};
